import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogTitle,
    DialogDescription,
    DialogHeader,
    DialogFooter,
    DialogClose,
} from '@/components/ui/dialog';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import EDatePicker from '@/components/everridge/EDatePicker';
import EEnumPopupPicker from '@/components/everridge/EEnumPopupPicker';
import Cancel_01_Icon from '@/components/icons/Cancel_01_Icon';
import { cn } from '@/lib/utils';
import { SuperAdminDashboardPageFilterFormSchema } from '@/features/superAdmin/features/dashboard/validators/SuperAdminDashboardPageFilterValidator';
import { useSuperAdminDashboardFilter } from '@/features/superAdmin/features/dashboard/store/useSuperAdminDashboardFilter';
import {
    LoginPeriod,
    UserActivityType,
} from '@/features/superAdmin/features/dashboard/types/super-admin-dashboard-dto';
import { subMonths } from 'date-fns';

const TODAY = new Date();

type FormData = z.infer<typeof SuperAdminDashboardPageFilterFormSchema>;

type Props = {
    onClose: () => void;
    isMobile?: boolean;
};

const SuperAdminDashboardPageFilter: FC<Props> = ({ onClose, isMobile }) => {
    const [isOpen, setIsOpen] = useState(true);
    const { t } = useTranslation();

    const { filter, setFilter } = useSuperAdminDashboardFilter();

    const { control, handleSubmit, reset } = useForm<FormData>({
        resolver: zodResolver(SuperAdminDashboardPageFilterFormSchema),
        values: {
            startDate: filter.startDate,
            endDate: filter.endDate,
            reportPeriod: filter.reportPeriod,
            activityType: filter.activityType,
        },
    });

    const handleClose = () => {
        setIsOpen(false);
        setTimeout(onClose, 300);
    };

    const handleSubmitFilterForm = (form: FormData) => {
        setFilter(form);
        handleClose();
    };

    const handleResetFilter = () => {
        reset({
            startDate: subMonths(TODAY, 6),
            endDate: TODAY,
            reportPeriod: LoginPeriod.Monthly,
            activityType: null,
        });
    };

    const reservationTypes = Object.values(LoginPeriod).filter(
        (value) => typeof value === 'string',
    );

    const activityTypes = Object.values(UserActivityType).filter(
        (value) => typeof value === 'string',
    );

    return (
        <Dialog open={isOpen} onOpenChange={handleClose}>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>
                        {t('elements.components.filter.title')}
                        <DialogClose
                            className={cn(
                                'ml-auto flex items-center justify-center cursor-pointer',
                                isMobile && 'size-11 border border-popover-100 rounded-full',
                            )}
                        >
                            <Cancel_01_Icon />
                        </DialogClose>
                    </DialogTitle>
                    <DialogDescription />
                </DialogHeader>
                <div className="flex flex-col gap-4">
                    <div className="flex gap-4">
                        <EDatePicker
                            control={control}
                            fieldName={'startDate'}
                            label={t('elements.form.startDate.label')}
                            placeholder={t('elements.form.startDate.placeholder')}
                        />
                        <EDatePicker
                            control={control}
                            fieldName={'endDate'}
                            label={t('elements.form.endDate.label')}
                            placeholder={t('elements.form.endDate.placeholder')}
                        />
                    </div>
                    <EEnumPopupPicker
                        control={control}
                        fieldName={'reportPeriod'}
                        label={t('elements.form.reportPeriod.label')}
                        placeholder={t('elements.form.reportPeriod.placeholder')}
                        list={reservationTypes}
                    />
                    <EEnumPopupPicker
                        control={control}
                        fieldName={'activityType'}
                        label={t('elements.form.activityType.label')}
                        placeholder={t('elements.form.activityType.placeholder')}
                        list={activityTypes}
                    />
                </div>
                <DialogFooter className={cn('justify-end', isMobile && 'mt-auto')}>
                    <Button variant="outline" onClick={handleResetFilter}>
                        {t('elements.buttons.reset')}
                    </Button>
                    <Button onClick={handleSubmit(handleSubmitFilterForm)}>
                        {t('elements.buttons.apply')}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export default SuperAdminDashboardPageFilter;
